import { useTranslation } from 'react-i18next';
import { z } from 'zod/v3';

import type { TextInputProps } from '@highlight-cp/core-ui';
import { TextInput } from '@highlight-cp/core-ui';

import ExternalLinkIcon from '../../action-icons/external-link-icon';

const DEFAULT_PROJECT_RECORD_BASE_URL =
  'https://app.hubspot.com/contacts/7306769/record/2-32975391/';

/**
 * A component that renders a text input for a HubSpot Projects record ID.
 * Optional: the hubspot-worker fills it in when it is blank, and replaces it only when it points
 * at a record that no longer exists in HubSpot.
 * @param rest - The rest of the props to pass to the text input.
 * @returns A text input for a HubSpot Projects record ID.
 */
const HubspotProjectIdInput = ({
  value,
  recordUrlBase = DEFAULT_PROJECT_RECORD_BASE_URL,
  ...rest
}: TextInputProps & {
  /** Base URL a project record id is appended to, for the HubSpot portal in use. Defaults to production. */
  recordUrlBase?: string;
}) => {
  const { t } = useTranslation();

  const isValidProjectId = value && /^\d{1,20}$/.test(value.toString().trim());

  const labelElement = isValidProjectId ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <ExternalLinkIcon
        href={`${recordUrlBase}${value.toString().trim()}`}
        title={t('Open HubSpot project in new tab')}
        size={14}
      />
      {t('Hubspot Project ID')}
    </span>
  ) : (
    t('Hubspot Project ID')
  );

  return (
    <TextInput
      description={t('Set automatically. Clear to have it re-populated on the next sync.')}
      label={labelElement}
      placeholder="123456789012"
      maxLength={20}
      value={value}
      {...rest}
    />
  );
};

/**
 * A zod validator for a HubSpot Projects record ID. Blank is allowed.
 * @returns A zod validator for a HubSpot Projects record ID.
 */
HubspotProjectIdInput.zodValidator = z
  .string()
  .regex(/^(\d{1,20})?$/, 'Hubspot Project ID must be numeric');

export default HubspotProjectIdInput;
